import { z } from 'zod';
import { openWorldCommandIdSchema } from './crossing';
import { openWorldFishRevealSchema } from './fish';

/**
 * **A pesca do mundo aberto: entrar na água, esperar, lutar, capturar.**
 *
 * ## O que é fixo, e o que é equilíbrio
 *
 * Fixo por decisão do dono: uma a três áreas por quadrante perigoso, **doze**
 * peixes por área, **um peixe recuperado a cada três minutos**, duas espécies
 * por raridade, e um peixe ocupa **um** espaço de carga. Tudo o resto — o raio
 * de uma área, o tempo até à mordida, os multiplicadores de raridade e a
 * frequência de cada raridade — é equilíbrio, vive em
 * `open-world/room/balance.ts` e é provisório.
 *
 * ## O segredo é a forma, e não a disciplina
 *
 * Antes da mordida **não há campo** para espécie, raridade, peso ou vida: a
 * tentativa pública diz apenas que alguém está a pescar. É por isso que cancelar
 * antes da mordida não serve para escolher prémio — não há nada para ver, e não
 * há nada que o servidor pudesse revelar por descuido.
 *
 * Depois da mordida, a revelação vai **só ao pescador**, pelo mesmo mecanismo
 * por conexão que a recusa de capacidade de um boss já usa.
 *
 * ## A água não é um obstáculo
 *
 * Uma área aquática não colide, não empurra, não trava e não fere. Ela é um
 * **lugar**: estar dentro dela é o que dá acesso a uma acção, e mais nada.
 *
 * ## A batalha tem prazo, e o peixe pode ganhar (gate V28I.X)
 *
 * Fixo por decisão do dono: a nave tem **três minutos** a contar da mordida para
 * derrotar o peixe. Vencido o prazo com o peixe vivo, ele **escapa** — sem
 * captura, sem item, sem Ouro — e o jogador pode também abandonar a qualquer
 * momento. Ver {@link OPEN_WORLD_FISH_FIGHT_LIMIT_MS}.
 *
 * Um peixe que escapa **não volta imediatamente à água**: a vaga esvazia e só
 * volta pela reposição normal. Devolvê-lo inteiro faria de desistir uma
 * ferramenta — relançar até calhar um exemplar vencível.
 */

/* -------------------------------------------------------------------------- */
/*  Os números fixos                                                          */
/* -------------------------------------------------------------------------- */

/** Quantos peixes uma área aquática guarda, no máximo. **Doze.** */
export const OPEN_WORLD_WATER_CAPACITY = 12;

/**
 * **Três minutos de relógio real por peixe recuperado.**
 *
 * Tempo de parede: uma área esvaziada por alguém que já saiu do quadrante
 * recupera na mesma, e reconectar não reinicia nada. Várias ausências formam
 * uma **fila** — três minutos pelo primeiro, mais três pelo segundo — e não um
 * reaparecimento em bloco.
 */
export const OPEN_WORLD_WATER_REPLENISH_MS = 3 * 60 * 1_000;

/**
 * **Três minutos para derrotar o peixe, e depois ele escapa** (gate V28I.X).
 *
 * O prazo da batalha automática, em milissegundos de relógio de parede. Começa
 * no instante autoritativo da **mordida** — não quando a ordem é dada, não
 * quando o painel aparece — e vencido sem a vida chegar a zero, o peixe escapa
 * sem recompensa nenhuma.
 *
 * ## Porquê um prazo, e não peixes mais fracos
 *
 * Porque a alternativa apagava a progressão. Desde o gate V28I.W a nave pesca com
 * a força REAL da conta, e a vida dos peixes tinha sido escrita contra um casco
 * normalizado: uma conta nova precisava de horas para um exemplar grande, sem
 * limite de tempo e sem fuga, e ficava presa. Baixar a vida dos peixes tiraria o
 * significado de ter uma nave melhor; um prazo mantém tudo:
 *
 * - naves fortes capturam peixes raros mais depressa;
 * - naves fracas ainda podem tentar;
 * - peixes acima da força daquela nave conseguem escapar;
 * - vida, raridade e peso continuam a decidir tudo.
 *
 * ## Porque NÃO é `OPEN_WORLD_WATER_REPLENISH_MS`
 *
 * Os dois valem três minutos hoje, e são **factos diferentes**: um é quanto
 * tempo uma nave tem para vencer um peixe, o outro é de quanto em quanto tempo
 * uma água recupera um. Partilhar a constante faria mover um mover o outro — e
 * afinar o prazo da batalha passaria a mexer na economia da água sem ninguém
 * pedir. Duas constantes, dois nomes, duas decisões.
 *
 * **Esta é a única definição.** Nenhum módulo escreve `180_000`, e o cliente
 * não a usa para decidir nada: o servidor publica o tempo que resta.
 */
export const OPEN_WORLD_FISH_FIGHT_LIMIT_MS = 3 * 60 * 1_000;

/** A versão do selo de um peixe. Muda o conteúdo de todas as gerações futuras. */
export const OPEN_WORLD_FISH_SEAL_VERSION = 2;

/* -------------------------------------------------------------------------- */
/*  As fases                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * **Em que ponto está uma pescaria.**
 *
 * `waiting` é o silêncio: a linha está na água, o peixe já existe e o pescador
 * não sabe nada dele. `fighting` começa na mordida, e é a única fase em que a
 * revelação existe.
 *
 * A mordida é também o instante em que o prazo de
 * {@link OPEN_WORLD_FISH_FIGHT_LIMIT_MS} começa a correr: uma espera longa nunca
 * gasta o tempo de luta de ninguém.
 */
export const OPEN_WORLD_FISHING_PHASES = ['waiting', 'fighting'] as const;

export type OpenWorldFishingPhase = (typeof OPEN_WORLD_FISHING_PHASES)[number];

/**
 * **Porque é que uma pescaria acabou.** Uma lista fechada, e mutuamente
 * exclusiva: uma tentativa tem UM desfecho autoritativo e nunca dois.
 *
 * ## O que acontece à vaga, por desfecho
 *
 * | desfecho | o peixe | a vaga |
 * | --- | --- | --- |
 * | `caught` | vira captura | esvazia, e a fila de reposição começa |
 * | `escaped` | foge | esvazia, e a fila de reposição começa |
 * | `abandoned` | foge | esvazia, e a fila de reposição começa |
 * | `left_water` | foge | esvazia, e a fila de reposição começa |
 * | `destroyed` | foge | esvazia, e a fila de reposição começa |
 * | `disconnected` | foge | esvazia, e a fila de reposição começa |
 * | `interrupted` | foge | esvazia, e a fila de reposição começa |
 * | `cargo_full` | **volta inteiro à água** | fica ocupada |
 *
 * **Um peixe que escapa não volta imediatamente à água** (gate V28I.X). Antes
 * deste gate qualquer fim que não fosse a captura devolvia o exemplar com a vida
 * cheia, e isso, com um prazo de três minutos, seria uma máquina: desistir e
 * relançar até calhar um peixe que a nave consegue vencer. A vaga volta pela
 * reposição normal — um peixe a cada três minutos — e a espécie seguinte é
 * selada de novo.
 *
 * `cargo_full` é a **única** excepção, e é deliberada: ali a nave venceu o peixe
 * e o que faltou foi espaço. Punir a vitória com a perda do exemplar seria
 * castigar a regra de capacidade, que não é o que este gate decidiu.
 */
export const OPEN_WORLD_FISHING_ENDINGS = [
  /** O jogador carregou em `ABANDONAR PESCA`. O peixe escapa. */
  'abandoned',
  /** O prazo de três minutos venceu com o peixe vivo. Ele escapa. */
  'escaped',
  /** O casco saiu da água — por vontade própria ou por empurrão. */
  'left_water',
  /** A nave morreu. */
  'destroyed',
  /** A ligação caiu. A tentativa acaba; a nave continua vulnerável. */
  'disconnected',
  /** Atravessou, extraiu, ou começou algo que não convive com pescar. */
  'interrupted',
  /** A carga encheu antes do golpe final. O peixe volta inteiro à água. */
  'cargo_full',
  /** Capturado. O único fim que produz uma linha de captura. */
  'caught',
] as const;

export type OpenWorldFishingEnding = (typeof OPEN_WORLD_FISHING_ENDINGS)[number];

/** Porque é que uma ordem de pescar foi recusada. */
export const OPEN_WORLD_FISHING_REFUSALS = [
  'destroyed',
  'not_in_water',
  'already_fishing',
  /** A área não tem nenhum peixe livre agora. */
  'no_fish',
  /** Nem um espaço de carga livre. */
  'no_space',
  /** A atravessar, a extrair, a canalizar, ou num porto. */
  'busy',
] as const;

export type OpenWorldFishingRefusal = (typeof OPEN_WORLD_FISHING_REFUSALS)[number];

/* -------------------------------------------------------------------------- */
/*  As ordens do cliente                                                      */
/* -------------------------------------------------------------------------- */

/**
 * **Começar a pescar.** Uma intenção, e nada mais.
 *
 * Note o que NÃO está aqui: a área, o peixe, a espécie, a raridade, o peso e o
 * dano. O servidor deriva a área da posição autoritativa da nave e escolhe o
 * peixe deterministicamente — o navegador não nomeia nada, portanto não há nada
 * para ele forjar.
 *
 * Leva `id` porque é uma ORDEM e não uma fotografia: ela é enviada uma vez, e
 * repeti-la tem de responder o mesmo em vez de começar uma segunda pescaria.
 */
export const openWorldFishStartSchema = z
  .object({
    t: z.literal('fs'),
    id: openWorldCommandIdSchema,
  })
  .strict();

export type OpenWorldFishStart = z.infer<typeof openWorldFishStartSchema>;

/**
 * **Abandonar a pescaria.** Uma ordem, e com a identidade da tentativa.
 *
 * ## Porque passou a ter `id` (gate V28I.X)
 *
 * Porque abandonar deixou de não decidir nada. Até este gate cancelar devolvia o
 * peixe inteiro à água, e repetir era literalmente o mesmo; agora o peixe
 * **escapa** e a vaga só volta pela reposição normal, portanto um segundo clique
 * atrasado podia matar uma pescaria diferente — a que o jogador começou entre os
 * dois.
 *
 * O `id` é o da ordem `fs` que começou a tentativa, e o servidor publica-o em
 * `o` na trama privada. A sala só encerra se ele **for** o da tentativa a
 * decorrer: um id repetido, atrasado ou de uma tentativa que já acabou não faz
 * nada, e não há nada para o cliente forjar — a conta vem do socket, e este
 * campo não é a identidade de ninguém.
 */
export const openWorldFishAbandonSchema = z
  .object({
    t: z.literal('fx'),
    /** A ordem `fs` que começou a tentativa a abandonar. */
    id: openWorldCommandIdSchema,
  })
  .strict();

export type OpenWorldFishAbandon = z.infer<typeof openWorldFishAbandonSchema>;

/* -------------------------------------------------------------------------- */
/*  A água, publicamente                                                      */
/* -------------------------------------------------------------------------- */

/**
 * **Uma área aquática na vista.**
 *
 * Posição, raio e uma semente visual — o suficiente para a desenhar, e nada
 * sobre o que está lá dentro. A **população não viaja**: saber quantos peixes
 * uma água tem sem lá ir seria saber onde vale a pena pescar sem pescar.
 *
 * A única excepção é o pescador, e mesmo a ele só depois de a área o recusar
 * por estar vazia.
 */
export const openWorldWaterAreaSchema = z
  .object({
    i: z.string().min(1).max(64),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    /** O raio, em milli-unidades. */
    r: z.number().int().min(1),
    /** Uma semente para a variação decorativa. Determinística, e sem segredo. */
    v: z.number().int().min(0),
  })
  .strict();

export type OpenWorldWaterArea = z.infer<typeof openWorldWaterAreaSchema>;

/**
 * **Alguém está a pescar**, dito a toda a gente que o vê.
 *
 * A entidade, a água e a fase. Um espectador fica a saber que aquela nave está
 * ocupada e onde a linha caiu — e mais nada. Nem a espécie, nem o peso, nem
 * quanto falta.
 */
export const openWorldFishingPublicSchema = z
  .object({
    /** A entidade do casco que está a pescar. */
    e: z.number().int().min(1),
    /** A água em que ele está. */
    a: z.string().min(1).max(64),
    p: z.enum(OPEN_WORLD_FISHING_PHASES),
    /** Onde a linha caiu, para o desenho a poder ligar à nave. */
    x: z.number().int().min(0),
    y: z.number().int().min(0),
  })
  .strict();

export type OpenWorldFishingPublic = z.infer<typeof openWorldFishingPublicSchema>;

/* -------------------------------------------------------------------------- */
/*  A tentativa, privadamente                                                 */
/* -------------------------------------------------------------------------- */

/**
 * **A minha pescaria**, e só a minha.
 *
 * Enviada apenas à conexão do pescador. O peixe (`f`) é `null` enquanto a fase
 * for `waiting` — não é um campo escondido, é um campo que ainda não tem valor,
 * e é isso que torna impossível revelar por descuido.
 */
export const openWorldFishingPrivateSchema = z
  .object({
    a: z.string().min(1).max(64),
    p: z.enum(OPEN_WORLD_FISHING_PHASES),
    /**
     * **A identidade desta tentativa**: a ordem `fs` que a começou.
     *
     * Publicada para o cliente poder abandonar **esta** e não a seguinte, e para
     * quem reconectou saber qual é a sua — o painel de uma reconexão não tem
     * outra maneira de a conhecer. Não é um segredo: é o id que o próprio
     * cliente cunhou.
     */
    o: openWorldCommandIdSchema,
    /** O peixe revelado, ou nada. Só existe depois da mordida. */
    f: openWorldFishRevealSchema.nullable(),
    /** A vida que resta, em milli, como decimal. Nula antes da mordida. */
    h: z.string().min(1).max(40).nullable(),
    /**
     * **Quantos milissegundos faltam até o peixe escapar.** Nulo antes da mordida.
     *
     * A AUTORIDADE. O cliente pode interpolar entre tramas para o contador não
     * saltar, mas o número que chega aqui substitui sempre a projeção local — e a
     * ausência dele **nunca** significa uma batalha infinita: um cliente que fale
     * um protocolo anterior é recusado no aperto de mãos, e não fica a adivinhar.
     */
    ms: z.number().int().min(0).nullable(),
  })
  .strict();

export type OpenWorldFishingPrivate = z.infer<typeof openWorldFishingPrivateSchema>;

/**
 * **O fim de uma tentativa**, dito ao pescador.
 *
 * Um resultado por tentativa, e **um só**: os desfechos são mutuamente
 * exclusivos e a sala emite exactamente uma destas tramas por `id`. `caught`
 * traz a captura para o cliente a poder mostrar o que apanhou; todos os outros
 * trazem apenas a razão.
 */
export const openWorldFishingResultSchema = z
  .object({
    t: z.literal('fr'),
    /** A ordem que começou esta tentativa. Repetir a ordem repete a resposta. */
    id: openWorldCommandIdSchema,
    e: z.enum(OPEN_WORLD_FISHING_ENDINGS),
    /** O que foi apanhado, quando o fim é `caught`. */
    c: openWorldFishRevealSchema.nullable(),
    /** Quantos espaços faltavam, quando o fim é `cargo_full`. */
    need: z.number().int().min(0).nullable(),
  })
  .strict();

export type OpenWorldFishingResult = z.infer<typeof openWorldFishingResultSchema>;

/** Uma ordem de pescar recusada. Ao pescador, com o id que ele enviou. */
export const openWorldFishingRefusalSchema = z
  .object({
    t: z.literal('ff'),
    id: openWorldCommandIdSchema,
    r: z.enum(OPEN_WORLD_FISHING_REFUSALS),
    /**
     * Quando faltam peixes, quanto falta para o próximo, em milissegundos.
     *
     * A única coisa que uma água diz sobre a população dela, e só a quem tentou
     * pescar nela: um contador para a reposição seguinte. Não diz quantos há.
     */
    in: z.number().int().min(0).nullable(),
  })
  .strict();

export type OpenWorldFishingRefusalFrame = z.infer<typeof openWorldFishingRefusalSchema>;

/* -------------------------------------------------------------------------- */
/*  A identidade da captura                                                   */
/* -------------------------------------------------------------------------- */

const SEPARATOR = '|';

/**
 * **O id da captura de um peixe**, derivado e nunca cunhado.
 *
 * Mundo, água, vaga e geração. **A conta não entra**: um peixe produz uma
 * captura, e a conta é o destinatário que a tentativa concluída decidiu. Se ela
 * entrasse na chave, duas tentativas de contas diferentes sobre o mesmo peixe
 * produziriam dois ids, e a idempotência deixaria de proteger o que existe para
 * proteger.
 *
 * A água já carrega o quadrante — o id dela é `{linha}:{coluna}:water_area:{n}`
 * — e a geração é a vida daquele exemplar.
 */
export function openWorldFishCatchEventId(input: {
  readonly worldId: string;
  readonly areaId: string;
  readonly slotOrdinal: number;
  readonly generation: number;
}): string {
  return [
    'fish-catch',
    input.worldId,
    input.areaId,
    String(input.slotOrdinal),
    String(input.generation),
  ].join(SEPARATOR);
}
